import { DateTime } from "luxon";
import { generateGoogleMapsUrl, GoogleMapsQuery } from "mt-shared/maps";
import styles from "./TransportAgreement.module.scss";
import { TransportAgreementProps } from "./index";

const OrganizedTourContract = ({ booking }: TransportAgreementProps) => {
  const parsedTime = DateTime.fromFormat(booking.group.time, "HH:mm");

  const removeLongVowelMarks = (text: string) => {
    const textWithoutMacrons = text.replace(/[āēīōūĀĒīōŪ]/g, (match) => {
      const map: Record<string, string> = {
        ā: "a",
        ē: "e",
        ī: "i",
        ō: "o",
        ū: "u",
        Ā: "A",
        Ē: "E",
        Ō: "O",
        Ū: "U",
      };
      return map[match] ?? match;
    });
    return textWithoutMacrons.replace(/ー/g, "");
  };

  const currency = booking.currency;

  const customTourPaymentAmount = () => {
    const paidTotal = booking.payments
      .filter((p) => p.state === "CONFIRMED")
      .reduce((sum, p) => sum + p.amount, 0);

    const unpaidTotal = booking.payments
      .filter((p) => p.state === "NEW")
      .reduce((sum, p) => sum + p.amount, 0);

    const total = paidTotal + unpaidTotal;

    return (
      <>
        {total} {currency}
      </>
    );
  };

  return (
    <div className={styles.MainConditions}>
      <h1 className={styles.Title}>Conditions of Organized Tour Contracts</h1>
      <p>
        This trip is an organized tour planned, recruited, and operated by
        MagicalTrip.Inc, the travel organizer (hereinafter referred to as
        &quot;our company&quot;). Customers will enter into an organized travel
        contract (hereinafter referred to as the &quot;Travel Contract&quot;)
        with our company.
      </p>
      <p>
        This document serves as part of the contractual documentation that is
        issued under Article 12-5 of the Travel Business Act upon the conclusion
        of the Travel Contract.
      </p>
      <p style={{ textAlign: "center" }}>
        (Explanation of travel transaction conditions pursuant to Article 12-4
        of the Travel Business Act)
      </p>

      <h2 style={{ marginBottom: 20 }}>[Main Travel Conditions]</h2>

      <div>
        <table>
          <tbody>
            <tr>
              <td>
                <strong>Tour Name:</strong>{" "}
                {booking.group.product.translation.name}
              </td>
              <td>
                <tr style={{ border: 0 }}>
                  <td>
                    <strong>Contract Date:</strong>{" "}
                    {(DateTime.fromISO(booking.created).isValid
                      ? DateTime.fromISO(booking.created)
                      : DateTime.fromFormat(booking.created, "yyyy-MM-dd HH:mm")
                    ).toFormat("yyyy/MM/dd")}
                  </td>
                </tr>
                <tr style={{ border: 0 }}>
                  <td>
                    <strong>Departure Date:</strong>{" "}
                    {DateTime.fromISO(booking.group.date).toFormat(
                      "yyyy/MM/dd",
                    )}
                  </td>
                </tr>
              </td>
            </tr>
          </tbody>
        </table>
        <table>
          <tbody>
            <tr>
              <td className={styles.SectionTitle}>
                <h3>Travel Schedule and Contents:</h3>
              </td>
              <td>
                <div>
                  {booking.pickupLocation ? (
                    <div style={{ marginBottom: 10 }}>
                      <strong>Pickup Location: </strong>
                      <br />
                      <span>{parsedTime.toFormat("h:mm a")}</span> <br />
                      {removeLongVowelMarks(booking.pickupLocation.name)} <br />
                      {removeLongVowelMarks(booking.pickupLocation.address)}
                      <a
                        className={styles.PickupMapLink}
                        href={generateGoogleMapsUrl(
                          booking.pickupLocation.query as GoogleMapsQuery,
                        )}
                        target="_blank"
                      >
                        {generateGoogleMapsUrl(booking.pickupLocation.query as GoogleMapsQuery)}
                      </a>
                    </div>
                  ) : (
                    <div>
                      <strong>Pickup Location: </strong> <br />
                      <span>{parsedTime.toFormat("h:mm a")}</span> <br />
                      <p>
                        {booking.group.product.translation.meetingPlaceName}
                        {booking.group.product.meetingPlaceGoogleMapsUrl && (
                          <>
                            -
                            <a
                              href={
                                booking.group.product
                                  .meetingPlaceGoogleMapsUrl ?? ""
                              }
                              target="_blank"
                              className={styles.PickupMapLink}
                            >
                              {booking.group.product.meetingPlaceGoogleMapsUrl}
                            </a>
                          </>
                        )}
                        <br />
                        {
                          booking.group.product.translation
                            .meetingPlaceDescription
                        }
                      </p>
                    </div>
                  )}
                  <div>
                    <strong>Itinerary:</strong>
                    <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                      {booking.group.customGroupItinerary?.length
                        ? booking.group.customGroupItinerary.map((step) => (
                            <li key={step.id}>{step.stepDescription}</li>
                          ))
                        : booking.group.product.translation.itinerary
                          ? booking.group.product.translation.itinerary
                              .split("\n")
                              .map((i, index) => <li key={index}>{i}</li>)
                          : null}
                    </ul>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={styles.TravelFeesTable}>
          <tbody>
            <tr>
              <td className={styles.SectionTitle}>
                <h3>Travel Fees:</h3>
              </td>
              <td className={styles.TravelFees}>
                <tr>
                  <td>
                    <strong>Travel fee:</strong>
                  </td>
                  <td>
                    <strong>Number people</strong>
                  </td>
                </tr>
                <tr>
                  <td>
                    {booking.group.product.isCustomTour ? (
                      <>{customTourPaymentAmount()}</>
                    ) : booking.paidAmount ? (
                      `${booking.paidAmount.amount} ${booking.paidAmount.currency}`
                    ) : (
                      `${booking.paymentAmount} ${currency}`
                    )}
                  </td>
                  <td>
                    {booking.details.reduce((sum, d) => sum + d.quantity, 0)}
                  </td>
                </tr>
              </td>
              <td>
                <p>
                  <strong>Note:</strong> <br />
                  When applying with an odd number of participants, seating
                  arrangements will be shared.
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        <table>
          <tbody>
            <tr>
              <td className={styles.SectionTitle}>
                <h3>Minimum Number of Participants:</h3>
              </td>
              <td>
                <p>
                  <strong>
                    {booking.group.product.minimumInGroup != null
                      ? `${booking.group.product.minimumInGroup} ${booking.group.product.minimumInGroup === 1 ? "Person" : "People"}`
                      : "—"}{" "}
                  </strong>
                  <br />
                  *If the number of participants does not reach the required
                  minimum, the trip may be canceled. In such cases, notice will
                  be given by 3 days prior to departure.
                </p>
              </td>
            </tr>

            <tr>
              <td className={styles.SectionTitle}>
                <h3>Travel Management:</h3>
              </td>
              <td>
                <p>
                  A tour conductor will not accompany the tour. However, a tour
                  guide provided by our company will accompany the tour.
                  <br />
                  If it becomes necessary to contact our company during the
                  course of the tour, please contact us using the telephone
                  number or email address listed in the "Organizer and Contact
                  Information" section of this document. Please note that the
                  accompanying tour guide is an employee of our company and is
                  not a licensed interpreter-guide under Japanese law.
                </p>
              </td>
            </tr>

            <tr>
              <td className={styles.SectionTitle}>
                <h3>Costs Included in the Travel Fee:</h3>
              </td>
              <td>
                <p>
                  The travel fee includes transportation costs, meal costs
                  (including tax and service charges), and sightseeing costs
                  (entrance fees, viewing fees) as specified in the travel
                  schedule.
                </p>
              </td>
            </tr>
            <tr>
              <td className={styles.SectionTitle}>
                <h3>Costs Not Included in the Travel Fee:</h3>
              </td>
              <td>
                <p>
                  Personal expenses incurred outside the travel schedule, such
                  as transportation, accommodation, and dining, are not
                  included. Optional travel insurance is also excluded.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <h2>Payment and Conclusion of the Travel Contract</h2>
      <table style={{ borderBottom: "1px solid #000" }}>
        <tbody>
          <tr>
            <td className={styles.SectionTitle}>
              <h3>Application Fee: (Travel Fee)</h3>
            </td>
            <td>
              <p>
                Please pay the full travel fee of{" "}
                <b style={{ fontSize: 18 }}>
                  {booking.group.product.isCustomTour ? (
                    <>{customTourPaymentAmount()}</>
                  ) : booking.paidAmount ? (
                    `${booking.paidAmount.amount} ${booking.paidAmount.currency}`
                  ) : (
                    `${booking.paymentAmount} ${currency}`
                  )}
                </b>{" "}
                at the time of application.
              </p>
            </td>
          </tr>
          <tr>
            <td>
              <h3>Conclusion of the Contract:</h3>
            </td>
            <td>
              <p>
                The contract is concluded upon our company&apos;s acceptance of
                your application and receipt of the application fee. This
                contract can only be concluded by the traveler themselves, and
                applications made by an agent on behalf of the traveler will be
                deemed invalid.
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <div className={styles.Section}>
        <div>
          <h2>Cancellation Policy</h2>
          <p>
            If customers cancel the Travel Contract for their reasons, the
            following cancellation fees will apply. Cancellations are accepted
            via email.
          </p>
          <table>
            <thead>
              <tr>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Classification
                </th>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Cancellation Fee
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  (a) If cancellation is made on or after the 20th day (10th day
                  in case of a one-day trip) counting back from the day prior to
                  the commencement of the tour (excluding the cases mentioned in
                  (b) through (e))
                </td>
                <td>20% or less of the tour fee</td>
              </tr>
              <tr>
                <td>
                  (b) If cancellation is made on or after the 7th day prior to
                  the day preceding the date of commencement of the tour
                  (excluding the cases mentioned in (c) through (e))
                </td>
                <td>30% or less of the tour fee</td>
              </tr>
              <tr>
                <td>
                  (c) If cancellation is made on the day preceding the date of
                  commencement of the tour
                </td>
                <td>40% or less of the tour fee</td>
              </tr>
              <tr>
                <td>
                  (d) If cancellation is made on the date of commencement of the
                  tour (excluding the case mentioned in (e))
                </td>
                <td>50% or less of the tour fee</td>
              </tr>
              <tr>
                <td>
                  (e) In case of cancellation after the commencement of the tour
                  or of nonparticipation without giving notification
                </td>
                <td>100% or less of the tour fee</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <h2>Organizer and Contact Information</h2>

          <p>
            <strong>[Certified Travel Licenses]</strong> Tokyo Metropolitan
            Government Office: No.2-7941 <br /> Thailand TAT Travel License:
            No.14/04706
          </p>
          <p>
            <strong>[Address]</strong> Tokyotatemono Yaesunakadori Bldg. 12F,
            1-4-10 Yaesu, Chuo, Tokyo, 103-0028 Japan
          </p>
          <p>
            <strong>[Phone]</strong> +81-50-1809-3996
          </p>
          <p>
            <strong>[Email]</strong> support@magical-trip.com
          </p>
          <p>
            <strong>[Business hour] 10:00am - 19:00pm</strong>
          </p>
          <p>
            <strong>[Certified General Travel Services Manager] </strong>
            Masayoshi Matsumoto
            <br />
            <span style={{ fontSize: 12 }}>
              *The Certified General Travel Services Manager is the person in
              charge of transactions at the office handling your travel
              arrangements. If you have any questions regarding this travel
              contract, please do not hesitate to contact us.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrganizedTourContract;
